import * as _ from "lodash";
import * as d3 from 'd3';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from "@angular/core";
import {Widget} from "@ispm/widgets/decorator/widget.decorator";
import {IWidgetItem} from "@ispm/widgets/interfaces/widget-item.interface";
import {
  AnalyticsChartSortOrder,
  AnalyticsChartType,
  IAnalyticsChartsConfig,
  IAnalyticsChartsConfigItem
} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-config.interface";
import {
  IAnalyticsChartDataTarget,
  IAnalyticsChartDataItem, IAnalyticsChartDataSource,
  IAnalyticsChartsData, AnalyticsChartCategory
} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-data.interface";
import {IAnalyticsChartsTooltipHandler} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-tooltip-handler.interface";
import {
  AnalyticsChartsBarVisualizer, AnalyticsChartsHBarVisualizer,
  AnalyticsChartsPieVisualizer,
  AnalyticsChartsVisualizer
} from "@ispm/widgets/components/analytics-charts/analytics-charts-visualizer";
import {
  AnalyticsChartsCategoryCollectorBuilder,
  AnalyticsChartsNumberCollectorBuilder,
  DataCollectType,
  IAnalyticsChartsDataCollector
} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-data-collector.interface";


@Component({
  selector: 'analytics-charts',
  templateUrl: './analytics-charts.component.html',
  styleUrls: ['./analytics-charts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('AnalyticsCharts')
export class AnalyticsChartsComponent implements OnInit, IWidgetItem<IAnalyticsChartsConfig>,
                                                  IAnalyticsChartDataTarget {

  @Input()
  public chartDataFeeder: IAnalyticsChartDataSource;

  @Input()
  public chartsConfig: IAnalyticsChartsConfig;

  @Input()
  public chartsData: IAnalyticsChartsData;

  @Input()
  public maxChartsInRow: number = 3;

  @Input()
  public tooltipHandler: IAnalyticsChartsTooltipHandler;

  // the default colors to use if not provided in configuration
  private defCC = [
    "#2B86B2", // [0] 40
    "#9DD3E3", // [1] 20
    "#BFE2EB", // [2] 15
    "#5DAFD1", // [3] 30
    "#1474A4", // [4] 45
    "#7CC0D9", // [5] 25
    "#1D4C84", // [6] 60
    "#DAF1F7", // [7] 10
    "#439AC1", // [8] 35
    "#176498", // [9] 50
    "#1A3670", // [10] 70
    "#1B578E", // [11] 55
    "#191D4F", // [12] 85
    "#F0F8FA", // [13] 5
  ];

  private def_color_pallet = [
    [this.defCC[0]],
    [this.defCC[1],  this.defCC[0]],
    [this.defCC[2],  this.defCC[3], this.defCC[4]],
    [this.defCC[2],  this.defCC[5], this.defCC[0], this.defCC[6]],
    [this.defCC[7],  this.defCC[1], this.defCC[8], this.defCC[9], this.defCC[10]],
    [this.defCC[7],  this.defCC[1], this.defCC[3], this.defCC[0], this.defCC[11], this.defCC[12]],
    [this.defCC[13], this.defCC[2], this.defCC[5], this.defCC[8], this.defCC[4],  this.defCC[6],   this.defCC[12]]
  ];

  private readonly parentElementId: string = "parent-an-charts-element";

  private chartsInternalId: string;
  private chartsTBodyId: string;
  private chartsInternalConfig: any;
  //=========================================================================================================

  constructor(private _changeDetector: ChangeDetectorRef) { }
  //=========================================================================================================

  ngOnInit() {

    if (this.chartsConfig) {
      this.onConfigChanged(this.chartsConfig);
    }

    // register as a listener to chart's dada change events
    this.chartDataFeeder.registerChartDataTarget(this);
  }
  //=========================================================================================================

  set widgetConfig(widgetConfig) {
      this.chartsConfig = widgetConfig.widgetPayload;
  }
  //=========================================================================================================

  set widgetData(widgetData: IAnalyticsChartsData) {
    this._changeDetector.detach();
    this.chartsData = widgetData;
    this._changeDetector.reattach();
  }
  //=========================================================================================================

  /**
   * create a container to hold the chart based on the provided configuration
   * @param chartConfig
   */
  createChartContainer(chartConfig: IAnalyticsChartsConfigItem, initBeforeCreate: boolean) {

    // create a new row to contain the chart, if needed
    let tRow;

    // get the table's rows body
    let tBodyElement = d3.select('#'+this.chartsTBodyId);

    // if it is indicated to init block - remove all current rows
    if (initBeforeCreate) {
      tBodyElement.selectAll('tr').remove();
    }

    // if there are rows and the number of cells in the last row do not exceed the maximum
    //  allowed, refer to this row. Otherwise, create a new row
    const tBodyRows = tBodyElement.selectAll('tr').nodes();
    if (tBodyRows && tBodyRows.length > 0) {

      let lastRow = tBodyRows[tBodyRows.length-1];

      let lastRowCols = lastRow.getElementsByTagName('td');

      if (lastRowCols && lastRowCols.length < this.maxChartsInRow) {
        tRow = d3.select(lastRow);
      }
    }

    // create a new row, if not exists
    if (! tRow) {
      tRow = tBodyElement.append("tr")
        .attr("class", "charts-table-row");
    }

    // create a new cell in the row
    let tCol = tRow.append("td")
      .attr("class", "charts-table-cell");

    // create a specific DIV element in the TD element
    const divId = this.chartsInternalId + "-chart-" + chartConfig.colId;
    let div = tCol.append("div")
      .attr("id", divId);
    //------------------------------------------------------------------------------------

    this.chartsInternalConfig[chartConfig.colId] = {
      id: chartConfig.colId,
      d3DivId: "#"+divId, // assign the ID to chartConfig's d3DivId
      data: chartConfig,
      chartCustomData: { // allow adding specific custom data that are required in the internal implementation
        dataCollector: null
      }
    };

  }
  //=========================================================================================================

  /**
   * return a list of IDs for all columns for which it is required to collect data
   */
  getCollectibleColumns(): Array<string> {

    return Object.keys(this.chartsInternalConfig);
  }
  //=========================================================================================================

  /**
   * this event is invoked whenever charts' configuration is changed
   */
  onConfigChanged (chartsConfig: IAnalyticsChartsConfig) {
    this.chartsConfig = chartsConfig;
    this.chartsInternalConfig = [];

    // get parent-container
    let parentElement = d3.select("#"+this.parentElementId);

    // get configured charts' data
    let chartData: any = this.chartsConfig ? this.chartsConfig.data : null;
    //-------------------------------------------------------------------------------------------------------

    // if no charts are configured, clear container and exit
    if (! chartData || Object.keys(chartData).length == 0) {
      parentElement.remove();
      return;
    }
    //-------------------------------------------------------------------------------------------------------

    // create a container to hold the chart based on the provided configuration:

    // if parent element exists, container was already initialized
    if (! parentElement || parentElement.size() == 0) {

      parentElement = d3.select('#section-an-charts-container')
        .append("div")
        .attr("id", this.parentElementId);

      // set the internal id by the given charts' header
      this.chartsInternalId = 'chartsInternal';

      if (this.chartsConfig.header) {
        this.chartsInternalId = this.chartsConfig.header.replace(/\s/g, "").toLowerCase();

        // append the container's header
        this.drawHeader(this.chartsConfig.header, parentElement);
      }

      // append the table that contains all the charts
      let table = parentElement
        .append("table")
        .attr("class", "an-charts-table");

      this.chartsTBodyId = this.chartsInternalId + "-charts-body";
      table.append("tbody")
        .attr("id", this.chartsTBodyId)
        .attr("class", "an-charts-body");

      // factor the tooltip handler
      // if a tooltip provider is given, use it. Else, create a default handler
      if (!this.tooltipHandler) {
        this.tooltipHandler = this.factorTooltipHandler(parentElement);
      }

    }
    //-------------------------------------------------------------------------------------------------------

    // create a container for each configured chart's data
    let initBeforeCreate: boolean = true;

    _.each(chartData, (item) => {
        this.createChartContainer(item, initBeforeCreate);
        initBeforeCreate = false;
    });
    //-------------------------------------------------------------------------------------------------------

  }
  //=========================================================================================================

  /**
   * this method is invoked by the charts-data-feeder whenever chart's data is being changed
   */
  onDataChanged (columnId: string, eventData: IAnalyticsChartDataItem) {

    // get the correlated configuration item for this id
    let internalConfig = this.chartsInternalConfig[columnId];

    // draw a chart for this column
    if (internalConfig) {
      this.drawChart(internalConfig, eventData);
    }

  }
  //=========================================================================================================

  /**
   * this method is invoked by the charts-data-feeder to set the charts' header
   */
  setHeader(data: any) {

    let header = this.chartsConfig ? this.chartsConfig.header : '';

    if (data) {
      header = data.header || header;

      if (data.prefix) {
        header = data.prefix + header;
      }

      if (data.suffix) {
        header = header + data.suffix;
      }

    }

    this.drawHeader(header);
  }
  //*********************************************************************************************************

  private drawHeader(header: string, parentElement?: any) {

    let headerId = "an-charts-header-element";
    let headerElement = d3.select("#" + headerId);

    // create the header element, if not exists
    if (! headerElement || headerElement.size() == 0) {

      // get parent-container
      parentElement = parentElement || d3.select("#" + this.parentElementId);

      headerElement = parentElement.append("span")
        .attr("id", headerId)
        .attr("class", "an-charts-header");

    }

    // set header's text
    headerElement.text(header);
  }
  //=========================================================================================================

  /**
   * this method is invoked by the charts-data-feeder to get the related data collector object for the given column
   */
  factorColumnDataCollector (columnId: string, dataType: string): IAnalyticsChartsDataCollector {

    // get the correlated configuration item for this id
    let internalConfig = this.chartsInternalConfig[columnId];

    // if internalConfig not found - return no implementation
    if (!internalConfig) {
      return null;
    }

    // get if data collector instance already factored for this column
    if (internalConfig.chartCustomData.dataCollector) {
      internalConfig.chartCustomData.dataCollector.resetCounters();

    } else {

      // get column's configuration
      let configItem = internalConfig.data;

      // factor an appropriate handler by the type of column
      let collectorBuilder = null;

      // if numeric data type:
      if (dataType == DataCollectType.Numeric) {

        // create a data-collector-builder and initialize it by the configuration
        collectorBuilder = new AnalyticsChartsNumberCollectorBuilder();

        // if range is defined in the configuration:
        if (!isNaN(configItem.categoriesRangeMin) && !isNaN(configItem.categoriesRangeMax)) {
          collectorBuilder.withRange(configItem.categoriesRangeMin, configItem.categoriesRangeMax);
        }

        // if number of categories is defined in the configuration:
        if (!isNaN(configItem.categoriesSize)) {
          collectorBuilder.withNumGroups(configItem.categoriesSize);
        }

        // if size of categories increment is defined in the configuration:
        if (!isNaN(configItem.categoriesIncrement)) {
          collectorBuilder.withIncrementSize(configItem.categoriesIncrement);
        }

        // if the specific categories are defined in the configuration:
        if (configItem.categories && configItem.categories.length > 0) {
          collectorBuilder.withGroups(configItem.categories);
        }

      // category column type
      } else {

        // create a data-collector-builder and initialize it by the configuration
        collectorBuilder = new AnalyticsChartsCategoryCollectorBuilder();

        // if number of categories is defined in the configuration:
        if (!isNaN(configItem.categoriesSize)) {
          collectorBuilder.withNumGroups(configItem.categoriesSize);
        }

        // if the specific categories are defined in the configuration:
        if (configItem.categories && configItem.categories.length > 0) {
          collectorBuilder.withGroups(configItem.categories);
        }

      }

      // build the dataCollector & set the data collector instance back to the column's internal data
      internalConfig.chartCustomData.dataCollector = collectorBuilder.build();

    }

    return internalConfig.chartCustomData.dataCollector;
  }
  //=========================================================================================================

  drawChart(chartInternalConfig: any, chartData: IAnalyticsChartDataItem) {

    let chartConfigItem: IAnalyticsChartsConfigItem;
    chartConfigItem = chartInternalConfig.data;

    // prepare the chart's data
    if (chartData.categories) {

      // factor the relevant chart handler
      let chartsBuilder: AnalyticsChartsVisualizer;

      if (chartConfigItem.chartType === AnalyticsChartType.pie) {
        chartsBuilder = new AnalyticsChartsPieVisualizer(true);

      } else if (chartConfigItem.chartType === AnalyticsChartType.bar) {
        if (chartConfigItem.verticalMode === true) {
          chartsBuilder = new AnalyticsChartsBarVisualizer();
        } else {
          chartsBuilder = new AnalyticsChartsHBarVisualizer();
        }

      } else {
        chartsBuilder = new AnalyticsChartsPieVisualizer(false);
      }
      //---------------------------------------------------

      let dataLength = chartData.categories.length;
      let dataArray: Array<any> = new Array(dataLength);
      let totalCount = 0;

      chartData.categories.forEach((category, index ) => {

        // Note: items with no counted data should be displayed anyway

        dataArray[index] = {category: category, color: ""};

        totalCount += category.count;
      });

      if (! isNaN(chartData.population)) {
        totalCount = chartData.population;
      }
      //---------------------------------------------------

      // set the colors pallet.

      // to ensure that the same colors will always be used for the same categories,
      //  sort the categories by their name
      dataArray.sort((obj1, obj2) => {
        if (obj1.category.label > obj2.category.label) return 1;
        if (obj1.category.label < obj2.category.label) return -1;
        return 0;
      });

      // if colors are not provided in configuration, use the default color pallet
      let colorPallet = chartConfigItem.colorsPallet;

      if (! colorPallet) {

        // set the color from the default colors palate
        let palletIndex = dataLength - 1;
        if (palletIndex >= this.def_color_pallet.length) {
          palletIndex = this.def_color_pallet.length - 1;
        }
        colorPallet = this.def_color_pallet[palletIndex];
      }

      for (let i = 0; i < dataArray.length; i++) {

        // if color's index exceeds the available length, repeat colors from the begining
        let colorIndex = i;
        if (colorIndex >= colorPallet.length) {
          colorIndex = (i % colorPallet.length);
        }

        dataArray[i].color = colorPallet[colorIndex];
      }
      //---------------------------------------------------

      // sort the values by the configuration: ASC / DESC / NONE
      // get the default sort order by the relevant chart handler
      let sortFactor: AnalyticsChartSortOrder = chartsBuilder.getDefaultSortOrder();
      if (chartConfigItem.sortOrder) {
        let orderStr = chartConfigItem.sortOrder.toLowerCase();

        sortFactor = (orderStr === "asc")? AnalyticsChartSortOrder.asc :
                     (orderStr === "desc")? AnalyticsChartSortOrder.desc :
                     AnalyticsChartSortOrder.none;
      }

      // if to sort values
      if (sortFactor != AnalyticsChartSortOrder.none) {

        // if sort order is ASC:
        //  (*) obj1 < obj2 --> return -1
        //  (*) obj1 > obj2 --> return 1

        // if sort order is DESC:
        //  (*) obj1 < obj2 --> return 1
        //  (*) obj1 > obj2 --> return -1

        dataArray.sort((obj1, obj2) => {

          // try to compare first by a custom comparable data.
          // if not available, compare by the number of population in each category
          let result = this.customCompareCategory(obj1.category, obj2.category, sortFactor);
          if (isNaN(result)) {
            result = this.compareCategoryValues(obj1.category.count, obj2.category.count, sortFactor);
          }
          if (result != 0) return result

          // values equal - compare by the label (this is important in case of having categories)
          if (obj1.category.label > obj2.category.label) return -1;
          if (obj1.category.label < obj2.category.label) return 1;

          return 0;
        });

      }
      //---------------------------------------------------

      let sortedData = {};
      let sortedKeys = [];
      let sortedColors = [];

      dataArray.forEach(item => {

        // calculate the category's percentage
        if (! item.category.percentage) {
          item.category.percentage = (item.category.count/totalCount*100).toFixed(2);
        }

        sortedData[item.category.label] = item.category;
        sortedKeys.push(item.category.label);
        sortedColors.push(item.color);
      });

      // build the internal data to pass
      let internalData: any = {
        header: chartConfigItem.colHeader,
        svgId: this.chartsInternalId + "-svg-chart-" + chartConfigItem.colId,
        data: sortedData,
        sortedKeys: sortedKeys,
        colors: sortedColors,
        dataLength: dataLength,
        total: totalCount
      };
      //---------------------------------------------------

      // invoke chartsBuilder to build the chart
      chartsBuilder
        .withTooltipHandler(this.tooltipHandler)
        .withConfig(chartInternalConfig)
        .withData(internalData)
        .buildChart();
    }

  }
  //=========================================================================================================

  customCompareCategory(cat1: AnalyticsChartCategory, cat2: AnalyticsChartCategory, sortFactor: AnalyticsChartSortOrder) {

    if (cat1.compareBy && cat2.compareBy ||
        (!isNaN(cat1.compareBy) && !isNaN(cat2.compareBy))) {

      // if the 'compareBy' is an instance of a comparator
      if (cat1.compareBy.hasOwnProperty('compare')) {
        return cat1.compareBy.compare(cat1, cat2, sortFactor);
      }

      return this.compareCategoryValues(cat1.compareBy, cat2.compareBy, sortFactor);
    }

  }
  //=========================================================================================================

  compareCategoryValues(cat1: any, cat2: any, sortFactor: AnalyticsChartSortOrder) {

    if (cat1 < cat2) {
      return 1 * sortFactor;
    } else if (cat1 > cat2) {
      return -1 * sortFactor;
    }
    return 0;
  }
  //=========================================================================================================

  factorTooltipHandler(chartsContainer: any): IAnalyticsChartsTooltipHandler {

    let tooltipElement = chartsContainer
      .append("div")
      .attr("id", "an-tooltip-element")
      .attr("class", "an-charts-tooltip")
      .style("opacity", 0);

    let chartsContainerElement = document.getElementById(this.parentElementId);

    let defTooltipHandler: IAnalyticsChartsTooltipHandler = {

      onMouseOut () {
        tooltipElement
          .transition()
          .duration(100)
          .style("opacity", 0);
      },

      onMouseOver (event: any, srcElement: any, htmlContent: string) {

        let rect = chartsContainerElement.getBoundingClientRect();

        tooltipElement
          .html(htmlContent)
          .style("left", (event.clientX - rect.left) + "px")
          .style("top", (event.clientY - rect.top-10) + "px");

        tooltipElement
          .transition()
          .duration(100)
          .style("opacity", 1);

      }
    };

    return defTooltipHandler;
  }
  //=========================================================================================================

}
